import { apiEndpoints } from '@/services/constants';
import httpServiceAuth from '@/services/http-service';
import { DateTime } from 'luxon';
import { errorHandler } from '@/services/error-handler';
import { max, min } from 'lodash';

function phpToLuxonFormat(format) {
  return format
    .replace(/YYYY/g, 'yyyy')
    .replace(/YY/g, 'yy')
    .replace(/DD/g, 'dd')
    .replace(/D/g, 'd')
    .replace(/MM/g, 'MM')
    .replace(/M/g, 'M')
    .replace(/HH/g, 'HH')
    .replace(/H/g, 'H')
    .replace(/hh/g, 'hh')
    .replace(/h/g, 'h')
    .replace(/mm/g, 'mm')
    .replace(/m/g, 'm')
    .replace(/ss/g, 'ss')
    .replace(/a/g, 'a')
    .replace(/A/g, 'a');
}

export default {
  state: {
    grid: [],
    date: DateTime.now().toFormat('yyyy-MM-dd'),
    slotDuration: null,
    loader: false,
    start: null,
    end: null,
    resource: null,
    selectedSlots: null,
    gridStart: '00:00',
    gridEnd: '00:00',
    gridBookings: null,
    gridResourceIds: [],
  },
  getters: {
    isValidDateTime: () => (from, to) => {
      const dateFrom = DateTime.fromISO(from).startOf('day');
      const dateTo = to ? DateTime.fromISO(to).endOf('day') : null;
      const dateNow = DateTime.now();
      return (dateNow > dateFrom && !dateTo) || (dateNow > dateFrom && dateNow < dateTo);
    },
  },
  mutations: {
    setGrid(state, payload) {
      state.grid = payload;
    },
    setDate(state, payload) {
      state.date = payload;
    },
    setSlotDuration(state, payload) {
      state.slotDuration = payload;
    },
    setLoader(state, payload) {
      state.loader = payload;
    },
    setStart(state, payload) {
      state.start = payload;
    },
    setEnd(state, payload) {
      state.setEnd = payload;
    },
    setResource(state, payload) {
      state.resource = payload;
    },
    setSelectedSlots(state, payload) {
      state.selectedSlots = payload;
    },
    setGridStart(state, payload) {
      state.gridStart = payload;
    },
    setGridEnd(state, payload) {
      state.gridEnd = payload;
    },
    setGridBookings(state, payload) {
      state.gridBookings = payload;
    },
    setGridResourceIds(state, payload) {
      state.gridResourceIds = payload;
    },
  },
  actions: {
    setGrid({ commit }, payload) {
      commit('setGrid', payload);
    },
    setSelectedSlots({ commit }, payload) {
      commit('setSelectedSlots', payload);
    },
    getGrid({ commit }, { presetId, date, loader }) {
      let url = `${apiEndpoints.company.bookingsGrid}/${presetId}?date=${date}`;
      if (loader) {
        commit('loader/setScreenLoading', true, { root: true });
      }

      return httpServiceAuth
        .get(url)
        .then((response) => {
          const gridData = response.data.data;
          let gridResourceIds = [];

          for (const res of gridData) {
            gridResourceIds.push(res.id);
            commit('setGridStart', min([res.start, this.gridStart]));
            commit('setGridEnd', max([res.end, this.gridEnd]));
          }
          commit('setGridResourceIds', gridResourceIds);
          commit('setGrid', gridData);
        })
        .finally(() => {
          if (loader) {
            commit('loader/setScreenLoading', false, { root: true });
          }
        });
    },
    getGridBookings({ commit }, { values, grid }) {
      let url = `${apiEndpoints.company.bookingsGridBookings}`;

      return httpServiceAuth.post(url, values).then((response) => {
        const gridBookingsData = response.data;

        let gridTemp = grid;

        // if (gridBookingsData.length == 0) {
        gridTemp = gridTemp.map((resource) => ({
          ...resource,
          bookings: [],
        }));
        commit('setGrid', gridTemp);
        // }

        for (const booking of gridBookingsData) {
          const resource = gridTemp.find((r) => r.id === booking.resource_id);
          if (!resource) continue;

          const tz = resource.timezone;
          const dateFormat = phpToLuxonFormat(resource.date_format) || 'd.M.yyyy';
          const timeFormat = phpToLuxonFormat(resource.time_format) || 'HH:mm';
          const format = `${dateFormat} ${timeFormat}`;

          const convert = (val) =>
            val ? DateTime.fromSQL(val, { zone: 'utc' }).setZone(tz).toFormat(format) : null;

          const toISO = (val) =>
            val
              ? DateTime.fromSQL(val, { zone: 'utc' })
                  .toUTC()
                  .toISO({ suppressMilliseconds: false, includeOffset: false }) + 'Z'
              : null;

          booking.auto_release = convert(booking.auto_release);
          booking.starts = convert(booking.starts);
          booking.ends = convert(booking.ends);
          booking.pre_check_in = convert(booking.pre_check_in);
          booking.expiring_warning = convert(booking.expiring_warning);
          booking.offset = 0;

          booking.datetime_from = toISO(booking.datetime_from);
          booking.datetime_to = toISO(booking.datetime_to);
          booking.limits = resource.limits;

          if (
            booking.previous_booking_in_minutes === null &&
            resource.resource_start &&
            booking.datetime_from
          ) {
            const starts = DateTime.fromISO(booking.datetime_from, { zone: 'utc' }).setZone(tz);

            if (starts.isValid) {
              const resourceStart = DateTime.fromFormat(resource.resource_start, timeFormat, {
                zone: tz,
              }).set({
                year: starts.year,
                month: starts.month,
                day: starts.day,
              });

              if (resourceStart.isValid) {
                booking.previous_booking_in_minutes = Math.max(
                  0,
                  Math.floor(starts.diff(resourceStart, 'minutes').minutes)
                );
              }
            }
          }

          if (
            booking.next_booking_in_minutes === null &&
            resource.resource_end &&
            booking.datetime_to
          ) {
            const ends = DateTime.fromISO(booking.datetime_to, { zone: 'utc' }).setZone(tz);

            if (ends.isValid) {
              const resourceEnd = DateTime.fromFormat(resource.resource_end, timeFormat, {
                zone: tz,
              }).set({
                year: ends.year,
                month: ends.month,
                day: ends.day,
              });

              if (resourceEnd.isValid) {
                booking.next_booking_in_minutes = Math.max(
                  0,
                  Math.floor(resourceEnd.diff(ends, 'minutes').minutes)
                );
              }
            }
          }
          resource.bookings.push(booking);
        }

        commit('setGrid', []);
        commit('setGrid', gridTemp);
        commit('setGridBookings', gridBookingsData);
      });
    },
    setGridBookings({ commit }, payload) {
      commit('setGridBookings', payload);
    },
    resize(
      { commit },
      {
        id: id,
        resource_id: resource_id,
        slots: slots,
        subject: subject,
        is_private: is_private,
        host_id: host_id,
      }
    ) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.company.bookingResize}`;
      return httpServiceAuth
        .put(url, {
          id: id,
          resource_id: resource_id,
          slots: slots,
          is_grid: true,
          subject: subject,
          is_private: is_private,
          host_id: host_id,
        })
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
    setDate({ commit }, payload) {
      commit('setDate', payload);
    },
    setSlotDuration({ commit }, payload) {
      commit('setSlotDuration', payload);
    },
    setLoader({ commit }, payload) {
      commit('setLoader', payload);
    },
    setStart({ commit }, payload) {
      commit('setStart', payload);
    },
    setEnd({ commit }, payload) {
      commit('setEnd', payload);
    },
    setResource({ commit }, payload) {
      commit('setResource', payload);
    },
    setDeclaration({ commit }, postObject) {
      commit('loader/setScreenLoading', true, { root: true });
      const url = `${apiEndpoints.company.bookings}/store`;
      return httpServiceAuth
        .post(url, postObject)
        .catch((error) => {
          if (error.response) {
            errorHandler(error.response);
          }
        })
        .finally(() => {
          commit('loader/setScreenLoading', false, { root: true });
        });
    },
  },
};
